import React, { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { FiHome, FiSearch, FiBook, FiMenu, FiX, FiMapPin } from 'react-icons/fi';
import { motion, AnimatePresence } from 'framer-motion';

const HOUSE_ADDRESS_LINE1 = "52 Highlands Ln";
const HOUSE_ADDRESS_LINE2 = "Beaver Creek, CO 81620";
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/CSL7ZuBo7w4fyGzc7";

const Layout = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const navigation = [
    { name: 'Home', href: '/', icon: FiHome },
    { name: 'Search', href: '/search', icon: FiSearch },
    { name: 'Guest Book', href: '/guestbook', icon: FiBook },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-snow-white relative">
      {/* Background Image - Desktop only */}
      <div
        className="hidden lg:block fixed inset-0 z-0"
        style={{
          backgroundImage: `url('/images/beaver-creek-landscape.jpeg')`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundAttachment: 'fixed'
        }}
      >
        {/* Subtle overlay for readability */}
        <div className="absolute inset-0 bg-white/[0.92]"></div>
      </div>
      
      {/* Content wrapper with z-index */}
      <div className="relative z-10">
      {/* Mobile Header */}
      <header className="lg:hidden sticky top-0 z-50 bg-white shadow-md">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-10 h-10 bg-lodge-brown-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-xl">BC</span>
            </div>
            <span className="font-display text-xl text-lodge-brown-800">Guidebook</span>
          </Link>
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="mobile-tap-target"
          >
            {mobileMenuOpen ? <FiX size={24} /> : <FiMenu size={24} />}
          </button>
        </div>
      </header>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="lg:hidden fixed inset-x-0 top-16 z-40 bg-white shadow-lg"
          >
            <nav className="p-4">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.href)
                      ? 'bg-lodge-brown-100 text-lodge-brown-800'
                      : 'hover:bg-gray-100'
                  }`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.name}</span>
                </Link>
              ))}
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-grow flex-col overflow-y-auto bg-lodge-brown-800 pt-5">
          <Link to="/" className="flex items-center px-4 mb-8">
            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
              <span className="text-lodge-brown-800 font-bold text-2xl">BC</span>
            </div>
            <span className="ml-3 text-white font-display text-2xl">Guidebook</span>
          </Link>
          <nav className="flex-1 space-y-1 px-2 pb-4">
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                  isActive(item.href)
                    ? 'bg-lodge-brown-900 text-white'
                    : 'text-lodge-brown-100 hover:bg-lodge-brown-700 hover:text-white'
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 flex-shrink-0 ${
                    isActive(item.href) ? 'text-white' : 'text-lodge-brown-300'
                  }`}
                />
                {item.name}
              </Link>
            ))}
          </nav>
          {/* Address Link */}
          <div className="px-4 py-4 border-t border-lodge-brown-700">
            <a
              href={GOOGLE_MAPS_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-start text-lodge-brown-200 hover:text-white transition-colors group"
            >
              <FiMapPin className="w-4 h-4 mr-2 mt-0.5 shrink-0 text-lodge-brown-400 group-hover:text-white" />
              <span className="text-xs leading-tight">
                {HOUSE_ADDRESS_LINE1}<br />
                {HOUSE_ADDRESS_LINE2}
              </span>
            </a>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className="lg:pl-64">
        <AnimatePresence mode="wait">
          <motion.div
            key={location.pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="px-4 sm:px-6 lg:px-8 py-6"
          >
            <Outlet />
          </motion.div>
        </AnimatePresence>
      </main>

      </div>

      {/* Mobile Bottom Navigation - outside relative z-10 wrapper for iOS fixed positioning */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 pb-safe-bottom">
        <div className="flex justify-around">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col items-center py-2 px-3 ${
                isActive(item.href)
                  ? 'text-lodge-brown-600'
                  : 'text-gray-400 hover:text-gray-700'
              }`}
            >
              <item.icon size={24} />
              <span className="text-xs mt-1">{item.name}</span>
            </Link>
          ))}
          {/* Address/Map Link */}
          <a
            href={GOOGLE_MAPS_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center py-2 px-3 text-gray-400 hover:text-gray-700"
          >
            <FiMapPin size={24} />
            <span className="text-xs mt-1">Map</span>
          </a>
        </div>
      </nav>
    </div>
  );
};

export default Layout;